import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import AgentDetails from "./AgentDetails";
import FloatingModal from "./FloatingModal";
import {
  insertOrUpdateAgent,
  removeAgent,
  getAgent,
} from "../../features/agent/agentSlice";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";
import {
  agentValidationSchema,
  getAgentInitialValues,
} from "../../constants/agent.constants";
import { Icons } from "../../constants/icon-library";
//import Configuration from "../Configuration/Configuration";
import { confirmationModal } from "../../constants/confirmation.constants";

const AgentTabs = () => {
  const { agentId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const modalRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const TAB_STORAGE_KEY = `agent_active_tab_${agentId}`;
  const baseRoute = `/agent/${agentId}`;
  const isNestedRoute = location.pathname !== baseRoute;

  const { user } = useSelector((state) => state.user);
  const { userSettings } = useSelector((state) => state.setting);
  const { selectedAgent } = useSelector((state) => state.agent);

  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem(TAB_STORAGE_KEY) || "details";
  });

  useEffect(() => {
    if (activeTab) {
      sessionStorage.setItem(TAB_STORAGE_KEY, activeTab);
    }
  }, [activeTab, TAB_STORAGE_KEY]);

  useEffect(() => {
    if (agentId) {
      dispatch(getAgent(agentId));
    }
  }, [agentId, dispatch]);

  useEffect(() => {
    if (user?.uuid) {
      dispatch(fetchSettingDetailsByUserId());
    }
  }, [user?.uuid, dispatch]);

  const assistantOptions = useMemo(() => {
    if (!userSettings?.length) return [];
    return userSettings.map((setting) => ({
      label: setting.assistantName || setting.assistantId?.name,
      value: setting.uuid,
      name: setting.assistantId?.name,
      platfrom: setting?.assistantId?.platform?.iconUrl
    }));
  }, [userSettings]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getAgentInitialValues(true, selectedAgent, user),
    validationSchema: agentValidationSchema,
    onSubmit: async (values) => {
      await dispatch(
        insertOrUpdateAgent({
          name: values.name,
          phoneNumber: values.phoneNumber,
          settingIds: values.assistants,
          uuid: selectedAgent?.uuid,
          accountId: user?.org,
          userId: user?.uuid,
          shortcut: selectedAgent?.shortcut,
        }),
      );

      toast.success("Agent updated successfully");
    },
  });

  const handleDelete = async () => {
    if (!selectedAgent?.uuid) return;

    const result = await confirmationModal({
      title: "Are you sure?",
      text: "This will permanently delete the agent.",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(removeAgent(selectedAgent.uuid)).unwrap();
        toast.success("Agent deleted successfully.");
        navigate("/agents");
      } catch (error) {
        toast.error("Failed to delete agent.");
      }
    }
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    navigate(baseRoute, { replace: true });
  };

  const tabs = [
    { key: "details", label: "Details", icon: "version" },
    // { key: "configuration", label: "Configuration", icon: "system-activity" },
  ];

  useEffect(() => {
    if (selectedAgent?.name) {
      setEditedName(selectedAgent.name);
    }
  }, [selectedAgent]);

  const saveName = async () => {
    if (!editedName.trim()) {
      toast.error("Agent name cannot be empty");
      return;
    }

    try {
      await dispatch(
        insertOrUpdateAgent({
          name: editedName,
          uuid: selectedAgent?.uuid,
          settingIds: selectedAgent?.settingIds || [],
          accountId: user?.org,
          userId: user?.uuid,
        }),
      ).unwrap();

      toast.success("Agent name updated");
      setIsEditing(false);
    } catch {
      toast.error("Failed to update name");
    }
  };
  
  return (
    <div className="content px-3">
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mt-3 mb-3 gap-2">
        <div className="d-flex align-items-center flex-grow-1">
          <div
            className="rounded-circle bg-gradient d-flex justify-content-center align-items-center me-3 flex-shrink-0"
            style={{
              width: "60px",
              height: "60px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
            }}
          >
            <img
              src="/assets/img/icons/agent.png"
              alt={selectedAgent?.name}
              style={{ width: "35px", height: "35px" }}
              className="grey-icon" 
            />
          </div>

          {!isEditing ? (
            <div className="d-flex align-items-center flex-grow-1">
              <h3 className="mb-0 me-2 text-truncate">
                {selectedAgent?.name}
              </h3>

              <button
                className="btn btn-sm"
                onClick={() => setIsEditing(true)}
              >
                <i className={`wb ${Icons("edit")}`} />
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2 flex-grow-1">
              <input
                type="text"
                className="form-control bg-secondary bg-opacity-25 border-secondary"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
              />

              <button className="btn btn-sm btn-success" onClick={saveName}>
                <i className={`wb ${Icons("correct")}`} />
              </button>

              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  setIsEditing(false);
                  setEditedName(selectedAgent?.name);
                }}
              >
                <i className={`wb ${Icons("cancel")}`} />
              </button>
            </div>
          )}
        </div>

        <button
          className="btn btn-primary"
          disabled={!selectedAgent?.settingIds?.length}
          onClick={() =>
            modalRef.current?.open({
              displayName: selectedAgent?.name,
              agentId: selectedAgent?.uuid,
              userId: user?.uuid,
            })
          }
        >
          <i className={`wb ${Icons("resume")} me-2`} />
          Use Now
        </button>
      </div>

      <div className="scrollable-tabs mb-3">
        <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
          {tabs.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                type="button"
                role="tab"
                className={`nav-link d-flex align-items-center ${
                  activeTab === tab.key ? "active" : ""
                }`}
                onClick={() => handleTabChange(tab.key)}
              >
                <i
                  className={`wb ${Icons(tab.icon)} fs-8 me-2 align-middle`}
                ></i>
                <span className="tab-label">{tab.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {isNestedRoute ? (
        <Outlet />
      ) : (
        <>
          {activeTab === "details" && (
            <AgentDetails
              formik={formik}
              assistantOptions={assistantOptions}
              user={user}
              navigate={navigate}
              handleDelete={handleDelete}
              isDefaultAgent={selectedAgent?.isDefault === true}
            />
          )}
          {/* {activeTab === "configuration" && (
            <Configuration configType="agent" configId={agentId} />
          )} */}
        </>
      )}

      <FloatingModal ref={modalRef} />
    </div>
  );
};

export default AgentTabs;